import React, { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import {
  UsersIcon,
  PencilIcon,
  TrashIcon,
  PlusIcon,
  BellIcon,
  CogIcon,
} from '@heroicons/react/24/outline';
import { GROUP_TYPE_LABELS, MEMBER_ROLE_LABELS } from './constants';
import LoadingSpinner from '../LoadingSpinner';
import { formatDisplayDate } from '../../utils/timeUtils';
import { isCoachInTrainingGroup } from '../../utils/roles';
import ReminderSettingsModal from './ReminderSettingsModal';
import { calendarAPI } from '../../services/api';

const GroupDetailsPanel = ({
  selectedGroup,
  groupDetails,
  isLoadingDetails,
  onShowMemberModal, // For adding members
  onShowEquipmentModal,
  onRemoveMember,
  onEditMember,
  onRemoveEquipment,
  currentUser, // To check for self-demotion
}) => {
  const [showReminderSettingsModal, setShowReminderSettingsModal] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(false);
  const isCoachForThisGroup = (() => {
    // Prefer authoritative membership list from groupDetails when available
    const group = groupDetails || selectedGroup;
    if (group?.user_memberships) {
      return group.user_memberships.some(
        (m) => m.user_id === currentUser?.id && m.role_in_group === 'coach'
      );
    }
    // Fallback to global user context membership
    return isCoachInTrainingGroup(currentUser, selectedGroup?.id);
  })();

  // Fetch calendar UUID to build user-specific Sheets URL
  const { data: calendarData } = useQuery('calendar_uuid', calendarAPI.getCalendarUuid, {
    enabled: isCoachForThisGroup,
    staleTime: 5 * 60 * 1000,
  });

  const sheetsExportUrl = useMemo(() => {
    if (!isCoachForThisGroup || !selectedGroup?.id) return '';
    const uuid = calendarData?.calendar_uuid;
    if (!uuid) return '';
    const baseUrl = window.location.origin;
    const params = new URLSearchParams({ group_id: String(selectedGroup.id), calendar_uuid: uuid });
    return `${baseUrl}/api/training/schedule/sheets-export?${params.toString()}`;
  }, [isCoachForThisGroup, selectedGroup, calendarData]);

  const handleCopyUrl = async () => {
    if (!sheetsExportUrl) return;

    try {
      await navigator.clipboard.writeText(sheetsExportUrl);
      setCopiedUrl(true);
      setTimeout(() => setCopiedUrl(false), 2000); // Reset after 2 seconds
    } catch (error) {
      console.error('Failed to copy URL:', error);
    }
  };
  if (!selectedGroup) {
    return (
      <div className="flex h-full items-center justify-center rounded-lg bg-white p-8 text-center shadow-md">
        <div>
          <UsersIcon className="mx-auto h-16 w-16 text-gray-400" />
          <h3 className="mt-2 text-lg font-medium text-gray-900">No Group Selected</h3>
          <p className="mx-auto mt-1 max-w-sm text-gray-500">
            Select a training group from the list to see its members, equipment, and details.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 lg:col-span-2">
      {/* Group Overview */}
      <div className="rounded-lg bg-white p-6 shadow-md">
        <div className="mb-4">
          <div>
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-bold text-gray-900">{selectedGroup.name}</h2>
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800">
                {GROUP_TYPE_LABELS[selectedGroup.group_type]}
              </span>
            </div>
          </div>
        </div>

        {selectedGroup.description && (
          <div className="mt-4">
            <p className="text-sm text-gray-600">{selectedGroup.description}</p>
          </div>
        )}

        <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="font-medium text-gray-900">Created:</span>{' '}
            <span className="text-gray-600">
              {selectedGroup.created_at
                ? formatDisplayDate(new Date(selectedGroup.created_at))
                : 'Unknown'}
            </span>
          </div>
          <div>
            <span className="font-medium text-gray-900">Last Updated:</span>{' '}
            <span className="text-gray-600">
              {selectedGroup.updated_at
                ? formatDisplayDate(new Date(selectedGroup.updated_at))
                : 'Unknown'}
            </span>
          </div>
        </div>
      </div>

      {/* Reminder Settings Section */}
      <div className="rounded-lg bg-white p-6 shadow-md">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BellIcon className="h-5 w-5 text-gray-500" />
            <h3 className="text-lg font-medium text-gray-900">Reminder Settings</h3>
          </div>
          {isCoachForThisGroup && (
            <button
              onClick={() => setShowReminderSettingsModal(true)}
              className="flex items-center gap-1 rounded bg-blue-500 px-3 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-blue-600"
            >
              <CogIcon className="h-4 w-4" />
              Edit Settings
            </button>
          )}
        </div>

        {isLoadingDetails ? (
          <LoadingSpinner />
        ) : (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">Automatic Reminders:</span>
              <span
                className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${(groupDetails || selectedGroup).availability_reminder_enabled
                    ? 'bg-green-100 text-green-800'
                    : 'bg-gray-100 text-gray-800'
                  }`}
              >
                {(groupDetails || selectedGroup).availability_reminder_enabled
                  ? 'Enabled'
                  : 'Disabled'}
              </span>
            </div>

            {(groupDetails || selectedGroup).availability_reminder_enabled && (
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Reminder Day:</span>
                <span className="text-sm text-gray-600">
                  {(() => {
                    const days = [
                      'Monday',
                      'Tuesday',
                      'Wednesday',
                      'Thursday',
                      'Friday',
                      'Saturday',
                      'Sunday',
                    ];
                    return (
                      days[(groupDetails || selectedGroup).availability_reminder_day] || 'Sunday'
                    );
                  })()}
                </span>
              </div>
            )}

            <div className="mt-4 rounded-md border border-blue-200 bg-blue-50 p-3">
              <p className="text-xs text-blue-800">
                {(groupDetails || selectedGroup).availability_reminder_enabled
                  ? `Members will receive automatic reminders to set their availability on ${(() => {
                    const days = [
                      'Monday',
                      'Tuesday',
                      'Wednesday',
                      'Thursday',
                      'Friday',
                      'Saturday',
                      'Sunday',
                    ];
                    return (
                      days[(groupDetails || selectedGroup).availability_reminder_day] || 'Sunday'
                    );
                  })()}s.`
                  : 'Automatic availability reminders are currently disabled for this group.'}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Google Sheets Export URL (coaches only) */}
      {isCoachForThisGroup && (
        <div className="rounded-lg bg-white p-6 shadow-md">
          <div className="mb-2 text-sm font-medium text-gray-900">Google Sheets URL</div>
          <div className="flex gap-2">
            <input
              type="text"
              readOnly
              value={sheetsExportUrl}
              className="w-full rounded border border-gray-300 bg-white px-2 py-1 text-xs text-gray-800 focus:outline-none"
              onFocus={(e) => e.target.select()}
            />
            <button
              className={`rounded px-2 py-1 text-xs font-medium transition-colors ${copiedUrl
                  ? 'bg-green-500 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              onClick={handleCopyUrl}
              type="button"
              title="Copy URL"
              disabled={!sheetsExportUrl}
            >
              {copiedUrl ? 'Copied!' : 'Copy'}
            </button>
          </div>
        </div>
      )}

      {/* Members Section */}
      <div className="rounded-lg bg-white p-6 shadow-md">
        {isLoadingDetails ? (
          <LoadingSpinner />
        ) : (
          <>
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">
                Members ({groupDetails?.user_memberships?.length || 0})
              </h3>
              {isCoachForThisGroup && (
                <button
                  onClick={onShowMemberModal}
                  className="flex items-center gap-1 rounded bg-blue-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-600"
                >
                  <PlusIcon className="h-4 w-4" />
                  Add Member
                </button>
              )}
            </div>

            {groupDetails?.user_memberships?.length === 0 ? (
              <div className="py-6 text-center">
                <p className="text-gray-500">No members in this group yet.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        Member
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        Role
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        Preferred Side
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        Joined
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {groupDetails.user_memberships.map((membership) => (
                      <tr key={membership.id}>
                        <td className="whitespace-nowrap px-6 py-4">
                          <div className="flex items-center">
                            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-200">
                              <span className="text-sm font-medium text-gray-600">
                                {membership.first_name?.[0]}
                                {membership.last_name?.[0]}
                              </span>
                            </div>
                            <div className="ml-3">
                              <div className="text-sm font-medium text-gray-900">
                                {membership.first_name} {membership.last_name}
                              </div>
                              <div className="text-xs text-gray-500">{membership.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-6 py-4">
                          <span
                            className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${membership.role_in_group === 'coach'
                                ? 'bg-purple-100 text-purple-800'
                                : membership.role_in_group === 'rower'
                                  ? 'bg-blue-100 text-blue-800'
                                  : 'bg-gray-100 text-gray-800'
                              }`}
                          >
                            {MEMBER_ROLE_LABELS[membership.role_in_group] ||
                              membership.role_in_group}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                          {membership.preferred_side === 'port'
                            ? 'Port'
                            : membership.preferred_side === 'starboard'
                              ? 'Starboard'
                              : 'No preference'}
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                          {membership.joined_at
                            ? formatDisplayDate(new Date(membership.joined_at))
                            : 'Unknown'}
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-right text-sm font-medium">
                          {isCoachForThisGroup ? (
                            <div className="flex justify-end gap-1">
                              <button
                                onClick={() => onEditMember(membership)}
                                className="p-1 text-gray-400 hover:text-gray-600"
                                title="Edit member role"
                              >
                                <PencilIcon className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => onRemoveMember(membership.id)}
                                className="p-1 text-gray-400 hover:text-red-600"
                                title="Remove member"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </div>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      {/* Equipment Section */}
      <div className="rounded-lg bg-white p-6 shadow-md">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">
            Assigned Equipment ({groupDetails?.equipment_assignments?.length || 0})
          </h3>
          {isCoachForThisGroup && (
            <button
              onClick={onShowEquipmentModal}
              className="flex items-center gap-1 rounded bg-blue-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-600"
            >
              <PlusIcon className="h-4 w-4" />
              Add Equipment
            </button>
          )}
        </div>
        {isLoadingDetails ? (
          <LoadingSpinner />
        ) : groupDetails?.equipment_assignments?.length === 0 ? (
          <div className="py-6 text-center">
            <p className="text-gray-500">No equipment assigned to this group yet.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Equipment
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Priority
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Notes
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {groupDetails.equipment_assignments.map((assignment) => (
                  <tr key={assignment.id}>
                    <td className="whitespace-nowrap px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">
                        {assignment.equipment?.name}
                      </div>
                      <div className="text-xs text-gray-500">{assignment.equipment?.type}</div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                      {assignment.priority}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">{assignment.notes || '-'}</td>
                    <td className="whitespace-nowrap px-6 py-4 text-right text-sm font-medium">
                      {isCoachForThisGroup && (
                        <button
                          onClick={() => onRemoveEquipment(assignment.equipment_id)}
                          className="p-1 text-gray-400 hover:text-red-600"
                          title="Remove equipment"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Reminder Settings Modal */}
      <ReminderSettingsModal
        show={showReminderSettingsModal}
        onClose={() => setShowReminderSettingsModal(false)}
        trainingGroup={groupDetails || selectedGroup}
      />
    </div>
  );
};

export default GroupDetailsPanel;
